import { Input } from '@mm/components';
import {
  KlaviyoAccessToken,
  tokenErrorMsg,
} from '@mm/state/model/ConnectionModel';
import classNames from 'classnames';
import React, { MouseEvent, useEffect, useState } from 'react';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';
import { KlaviyoErrorMessage } from './KlaviyoErrorMessage';
import styles from './KlaviyoIntegrations.module.css';

type Props = {
  token: KlaviyoAccessToken | undefined;
  helpLink: string;
  createApiKey: (privateKey: string) => void;
  updateApiKey: (id: number, privateKey: string) => void;
  hasNativeIntegrationsFeature: boolean | undefined;
  showUpgradeModal: (onSuccessfulBilling?: () => void) => void;
  controlsDisabled: boolean;
};

export const KlaviyoApiKeyConnection = (props: Props) => {
  const {
    token,
    helpLink,
    hasNativeIntegrationsFeature,
    createApiKey,
    updateApiKey,
    controlsDisabled,
    showUpgradeModal,
  } = props;
  const privateKey = token?.privateKey;
  const [apiKey, setApiKey] = useState(token?.privateKey);
  const [apiKeyError, setApiKeyError] = useState('');

  const disabledApiKey = !!(privateKey && apiKey === privateKey);

  useEffect(() => {
    if (token?.status === 'error') {
      setApiKeyError(tokenErrorMsg('apiKey', token.errorInfo));
    }
  }, [token]);

  useEffect(() => {
    setApiKey(privateKey);
  }, [privateKey, setApiKey]);

  const connectApiKey = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (!apiKey?.startsWith('pk_') || apiKey.length < 21) {
      setApiKeyError('Invalid API Key');
      return;
    }

    if (!hasNativeIntegrationsFeature) {
      showUpgradeModal(connectApiKey);
    } else {
      if (token) {
        updateApiKey(token.id, apiKey);
      } else {
        createApiKey(apiKey);
      }
      setApiKeyError('');
    }
  };

  return (
    <div
      className={classNames(styles.box, {
        [styles.boxDisabled]: controlsDisabled,
      })}
    >
      <div className={styles.boxWithHelp}>
        <div>
          <div className={styles.heading}>API Key</div>
          <div className={styles.apiKeyInput}>
            <Input
              placeholder="Enter your private API Key"
              css={{ container: styles.inputContainer, input: styles.input }}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
            />
            <PrimaryButton
              className={styles.primaryButton}
              onClick={e => connectApiKey(e)}
              disabled={disabledApiKey}
            >
              {token && disabledApiKey
                ? 'Connected'
                : token
                ? 'Update'
                : 'Connect'}
            </PrimaryButton>
          </div>
        </div>
        <HelpCenter helpLink={helpLink} className={styles.help} />
      </div>
      {apiKeyError && (
        <KlaviyoErrorMessage
          classNames={classNames(
            styles.syncWarningBox,
            styles.apiKeyWarningBox,
          )}
          error={apiKeyError}
        />
      )}
    </div>
  );
};
